// product-resolver.js — Pure client-side cost resolver extracted from ProductsView.jsx.
//
// Exports:
//   buildRefDisplay(c)                          — display code for a comp ref
//   evalFormula(expr, env, errRef?)             — re-export of the SHARED evaluator (src/formula.js)
//   sizeDisplay(c, env)                         — { text, marker, error } for Size column
//   clientLaborAddon(comp|l1, line_hrs, eff, laborRate, laborProductivity) — { lh, lc, rate }
//   resolveComps(comps, params, localValues, laborRate, laborProductivity) → resolved comp array

import { evalFormula } from '../formula.js';
// THE param environment builder, shared with the server resolver — so an internal
// or derived (formula) parameter resolves to the same number on both sides. This
// used to be two inline lines here and four more in product-db.js; five copies is
// how twins drift. See param-env.js.
import { buildParamEnv, isConfigurable } from '../param-env.js';
export { evalFormula };
export { isConfigurable };

// ---------------------------------------------------------------------------
// buildRefDisplay — short code for the Ref column.
// Short code = l3+l4 (matches catalog Code column), trimmed size appended.
// Handles both 'line_size' (DB alias, post-B2) and legacy 'size' (frozen snapshots).
// For sized comps (SizedRefId), ref_id is id-based ("fam:{id}"); the family's
// l3+l4 code is embedded server-side as c._family_display. Legacy "l1|l2|l3|l4"
// paths (frozen snapshots / pre-migration) are split as a defensive fallback.
// ---------------------------------------------------------------------------
export function buildRefDisplay(c) {
  if (c.size_mode === 'sized') {
    if (c._family_display) return `${c._family_display} *`;
    const parts = (c.ref_id || '').split('|');
    return `${parts[2] || ''}${parts[3] || ''} *`;
  }
  // Sub-product ref: show the sub-product's code (e.g. "E1454"), not the raw id.
  if (c.kind === 'product') return c._sub_code || c.ref_id;
  const sz = c.line_size || c.size;
  return c.l3
    ? `${c.l3}${c.l4}` + (sz ? ` ${sz.trim().replace(/\s+/g, ' ')}` : '')
    : c.ref_id;
}

// ---------------------------------------------------------------------------
// sizeDisplay — compute display info for a comp's Size column.
// Returns { text: string|null, marker: string|null, error: string|null }
//   text:   the resolved value to display (e.g. '12"', '3in', 'NA')
//   marker: short formula / param name shown as ↳marker (e.g. 'Size', 'BoothHeight * 0.8')
//   error:  error string when formula fails — triggers ⚠ rendering
// ---------------------------------------------------------------------------
// Two modes reach here, because two are all a component can be in: size_mode is
// DERIVED from the stored shape since 2026-07-29 (see COMP_SIZE_MODE_SQL). The
// 'literal', 'configure' and 'bind' branches went with the column — 0 rows used
// them, and 'bind' displayed a value it then applied to the QUANTITY.
export function sizeDisplay(c, env) {
  const mode = c.size_mode || 'NA';
  if (mode === 'NA') {
    // A PINNED comp is not size-LESS — it knows its size exactly; it just does not
    // FOLLOW a parameter. It rendered the literal text "NA" anyway, which is how
    // three sync-created comps sat on screen saying nothing while their refs said
    // 6" (150) one column over (Alex, 2026-08-17 → 08-19: "the size is still N/A.
    // What should we do?"). The pinned line's standardized look is already embedded
    // (line_size); show it. No ↳marker — a marker means "computed from", and a pin
    // is the absence of that.
    if (c.line_size) return { text: String(c.line_size).trim().replace(/\s+/g, ' '), marker: null, error: null };
    return { text: 'NA', marker: null, error: null };
  }
  if (mode === 'sized') {
    if (!c.size_expr) return { text: '—', marker: null, error: null };
    const errRef = {};
    const sizeVal = evalFormula(c.size_expr, env, errRef);
    const expr = c.size_expr.replace(/^\s*=\s*/, '');
    if (sizeVal == null) return { text: null, marker: expr, error: errRef.msg || `unresolved: ${expr}` };
    const line = (c.sized_lines || []).find(l => l.size_value != null && Math.abs(l.size_value - sizeVal) < 0.001);
    if (!line) return { text: `${sizeVal}"`, marker: expr, error: `no catalog line for ${sizeVal}"` };
    // S2.1 Fix 2: show the resolved line's STANDARDIZED look (catalog `size` col, e.g.
    // `3" (80)`) rather than the bare nps (`3"`). Display-only — the resolved line, cost,
    // and size_expr are unchanged (resolution above is untouched). Falls back to `<nps>"`
    // for legacy lines with no standardized `size` value.
    const look = (line.line_size || `${line.size_value}"`).trim().replace(/\s+/g, ' ');
    return { text: look, marker: expr, error: null };
  }
  return { text: '—', marker: null, error: null };
}

// ---------------------------------------------------------------------------
// clientLaborAddon — per-row install labor for lines with `line_hrs`.
// Rates come from the DB (trades + labor_rates@US_GA) — the server is the one
// source of truth (Q5). App.jsx fetches /api/options/trades once on mount and
// calls setTradeRates(); until then _tradeRates is empty and labor is skipped
// (with a warning) rather than guessed from a stale hardcoded copy.
// RM-class lines → trade rate from the product's trade
// HR-class lines → hours = eff (qty IS hours; cost already correct)
// MF-class lines → skip (have their own inst_rate/output_rate)
// ---------------------------------------------------------------------------
let _tradeRates = {};

// CostClass (Alex, 2026-07-02): L1 CODES are user-editable (Alex renamed all six:
// '0-RM'→'RM', '1-HR'→'HR', '3-MF'→'MF', etc.) — the client must never key off them
// again. The server embeds the stable `_l1_class` on every comp (see product-db.js
// getProduct); this legacy map is a fallback ONLY for comps that predate that embed
// (e.g. frozen placement snapshots saved before this fix) where `_l1_class` is absent
// but the OLD literal l1 code is still in the frozen JSON.
const LEGACY_L1_CLASS = { '0-RM': 'RM', '1-HR': 'HR', '2-PE': 'PE', '3-MF': 'MF', '5-SC': 'SC', 'E-MAT': 'EX' };

// classOf(c) — resolve a comp's cost_class: prefer the server-embedded _l1_class,
// else fall back to the legacy literal-code map, else undefined (unclassed/unknown).
function classOf(c) {
  if (c._l1_class) return c._l1_class;
  return c.l1 != null ? LEGACY_L1_CLASS[c.l1] : undefined;
}

// map: { trade_id: rate_per_hour } — call once after fetching /api/options/trades.
export function setTradeRates(map) {
  _tradeRates = map || {};
}

// CostClass (Alex, 2026-07-02): takes the L1 CODE (unchanged call signature) but
// classifies via classOf({ l1 }) — legacy-map fallback for frozen pre-fix snapshots.
// 4th arg is now the RESOLVED RATE, not a trade (Alex, 2026-07-18). The server
// works it out from the product's labour LINE and embeds it as `_labor_rate`
// (see product-db.js resolveLaborSource), so this twin no longer does its own
// lookup — there is one authority for the figure instead of two that could
// drift. `_tradeRates`/setTradeRates are retained only for backward tolerance
// while any caller still passes a trade string.
// 5th arg (NIGHT_SHIFT.md item 5, 2026-07-19) is the crew's PRODUCTIVITY, which
// DIVIDES the hours — server-resolved and embedded as `_labor_productivity`,
// same single-authority reasoning as the rate. Omitted/invalid falls back to
// 1.0, an exact no-op.
export function clientLaborAddon(l1OrComp, line_hrs, eff, laborRate, laborProductivity) {
  // Takes the COMP (or a bare l1, for older callers). It used to take only the l1
  // string and rebuild `{ l1 }`, which THREW AWAY the server-embedded `_l1_class` --
  // so classOf fell through to the legacy literal-code map, which has no 'HR' key,
  // only the pre-rename '1-HR'. Alex renamed the L1 codes, so from that day every
  // HR component classified as undefined: its cost went to mat_cost and Hours,
  // $/Hr and Labor all rendered blank. 11 components, $112k, shown as material.
  // The irony is that the comment on LEGACY_L1_CLASS says the client must never key
  // off codes again — and this call site was doing exactly that.
  const cls = (l1OrComp && typeof l1OrComp === 'object') ? classOf(l1OrComp) : classOf({ l1: l1OrComp });
  if (cls === 'MF') return { lh: null, lc: 0, rate: null };
  if (cls === 'HR') return { lh: eff, lc: 0, rate: null };
  if (!line_hrs || line_hrs <= 0) return { lh: null, lc: 0, rate: null };
  // tolerate a legacy trade string until every caller passes the embedded rate
  const rate = typeof laborRate === 'number' ? laborRate
             : (laborRate ? _tradeRates[laborRate] : null);
  if (rate == null) {
    // `l1` here was a ReferenceError, not a bad message: when this function's
    // parameter became `l1OrComp` (the HR bucketing fix, 2026-07-31) the template
    // kept referring to the old name. Nothing caught it because the branch only runs
    // for a comp that has HOURS but no resolvable labour rate — none existed until
    // 2026-08-02. It then threw inside resolveComps and took the whole ProductsView
    // down: the screen rendered, then blanked.
    // `uncharged` is the server twin's (laborAddon): the hours are still dropped, but
    // the grid can now say how many, beside the row and above the grid.
    return { lh: null, lc: 0, rate: null, uncharged: Math.round(line_hrs * eff * 10000) / 10000 };
  }
  const prod = (typeof laborProductivity === 'number' && laborProductivity > 0) ? laborProductivity : 1.0;
  const lh = Math.round(line_hrs * eff / prod * 10000) / 10000;
  const lc = Math.round(lh * rate * 100) / 100;
  return { lh, lc, rate };
}

// ---------------------------------------------------------------------------
// resolveComps — client-side resolver (BUILD 2/GridRefinements).
//   qty_expr set  → eff = evalFormula(qty_expr, paramVals) × factor  (CASCADE)
//   ratio driver  → eff = base_qty × (actual / typical) × factor
//   unit          → eff = base_qty × factor
// ---------------------------------------------------------------------------
// UNIT COST FOR A SUB-PRODUCT ROW (Alex, 2026-08-20: "the unit prices are
// missing", on a TTX x 8 row showing Supply $272,000 and a dash where every
// catalog row shows its each-price).
//
// Not a new number and not an invented one: the Unit Cost column already means
// exactly "Supply divided by Qty" everywhere else -- Std RB reads $4,500 with
// 114 each and $513,000 supply. A sub-product had no `line_price` only because
// that field is read off a CATALOG LINE, and a sub-product is not one. So the
// column dashed on the one row where the per-unit figure is most worth seeing:
// what one assembled unit costs ($34,000 for a TTX).
//
// For a QUANTITY-BASED sub-product it stays just as true and reads better still:
// qty is the driver's magnitude, so this is cost per kg / per m2, and the Unit
// column beside it already shows that driver's unit.
//
// ⚠ Supply only, never supply+labour -- matching the column's meaning on every
// other row. Labour has its own columns, and folding it in here would make one
// row's Unit Cost x Qty stop equalling its Supply.
const subUnitCost = (mat_cost, qty) =>
  (mat_cost != null && Number.isFinite(qty) && qty !== 0)
    ? Math.round((mat_cost / qty) * 100) / 100
    : null;

export function resolveComps(comps, params, localValues, laborRate, laborProductivity) {
  const paramMap = {};
  for (const p of params) paramMap[p.name] = p;
  // Simulate values (localValues) are overrides, so they land on configurable
  // params only — an internal param is not something the Simulate column offers.
  const { env } = buildParamEnv(params, localValues);

  return comps.map(c => {
    const ref_display = buildRefDisplay(c);
    const { text: sizeText, marker: sizeMarker, error: sizeDispErr } = sizeDisplay(c, env);
    // Product comp whose sub size param is bound via param_map: prefer the server-embedded
    // look/marker (S3 size-display) over sizeDisplay()'s 'NA' (which doesn't understand
    // param_map — see product-db.js getProduct's hasMap block).
    const size_display = (c.kind === 'product' && c._size_display != null) ? c._size_display : sizeText;
    const size_marker  = (c.kind === 'product' && c._size_display != null) ? c._size_marker  : sizeMarker;

    const NO_COST = { mat_cost: null, labor_rate: null, labor_cost: null, line_cost: null, hours: null };

    // kind='product': sub-assembly — cost from _sub_resolved embedded by the server (A43).
    if (c.kind === 'product') {
      // effective_qty (the driven param VALUE for a driver comp; else the multiplier):
      // qty_expr → evalFormula; else base_qty × factor.
      let effective_qty;
      if (c.qty_expr) {
        const qtyErrRef = {};
        effective_qty = evalFormula(c.qty_expr, env, qtyErrRef);
        if (effective_qty == null) {
          const ferr = qtyErrRef.msg || `qty unresolved: ${c.qty_expr}`;
          return { ...c, ref_display, size_display, size_marker, formula_error: ferr, effective_qty: null, ...NO_COST, _is_sub_assembly: true };
        }
      } else {
        effective_qty = c.base_qty;
      }
      effective_qty = Math.round(effective_qty * 10000) / 10000;

      // Q8 v2 — param_map comp: the server embeds _sub_resolved_mapped, the sub's
      // resolution AT the parent's TYPICAL env (generalizes v1's _sub_resolved_driven
      // to N mapped params — see FORMULAS.md). At the parent's TYPICAL values, this is
      // exact. At any OTHER LOCAL value, the mapping formulas themselves are exact
      // (they're formulas over PARENT params — evalFormula runs them here), but the
      // client can't re-resolve the SUB's own (possibly non-linear) formula graph, so
      // it APPROXIMATES linearly off the typical-mapped point and flags the row with
      // a "≈ linear preview" note — exact cost lands on save (server recomputes).
      // Multi-param blend: the mean of each mapped sub-param's own ratio
      // (nowValue ÷ typical-mapped-value) — degenerates to the v1 single-ratio case
      // when only one param is mapped.
      // QUANTITY-BASED sub — Qty is the SIZE, not a count, so the sub is priced AT
      // that size and NOT multiplied (subCostShape, param-env.js). Checked BEFORE the
      // param_map branch because the driver wins over a mapping either way.
      //
      // This branch exists because the Product screen does not use the server's
      // resolver — it computes here. Wiring the four server paths and stopping was
      // exactly why Alex saw "Nothing did change!": the database was right and the
      // screen was still multiplying.
      //
      // The client cannot re-resolve the sub's formula graph, so it scales the
      // server's typical resolution by size ÷ typical — exact for a linear product,
      // and the same approximation the mapped branch below already documents.
      if (c._sub_qty_param) {
        // ⚠ SCALE FROM THE SUB AT DRIVER = 1, NOT FROM ITS TYPICAL (Alex, 2026-08-12:
        // "when a parameter typical is 0, and used in product Qty, the price is 0 even
        // if qty = 100"). Scaling by `size ÷ typical` off `_sub_resolved` had TWO
        // failures at once when the typical was 0: the base resolution was itself 0,
        // and the divide-by-zero guard turned the ratio into 1. Product of the two:
        // $0.00 for every Qty, with no error — the server meanwhile said 1,264.83.
        // Measured on OVI's C/m2, whose WallArea typical is 0.
        //
        // A unit base cannot degenerate: 1 is always a usable size. `_sub_resolved`
        // stays the fallback for a DB written before the server embedded the unit
        // resolution, so an old payload still prices the way it used to rather than
        // showing nothing.
        const atOne = c._sub_qty_at_one;
        const useUnit = !!atOne && atOne.mat != null;
        const sub = useUnit ? atOne : c._sub_resolved;
        if (sub?.cycle_error) {
          return { ...c, ref_display, size_display, size_marker, formula_error: sub.cycle_error, effective_qty, ...NO_COST, _is_sub_assembly: true };
        }
        if (!sub || sub.mat == null) {
          return { ...c, ref_display, size_display, size_marker, formula_error: null, effective_qty, ...NO_COST, _is_sub_assembly: true };
        }
        const typ = Number(c._sub_qty_typical);
        const ratio = useUnit
          ? effective_qty                                    // base is one unit of the driver
          : (Number.isFinite(typ) && typ !== 0 ? (effective_qty / typ) : 1);
        const mat_cost   = Math.round(sub.mat   * ratio * 100) / 100;
        const labor_cost = sub.labor > 0 ? Math.round(sub.labor * ratio * 100) / 100 : null;
        const line_cost  = Math.round((mat_cost + (labor_cost ?? 0)) * 100) / 100;
        const hours      = sub.hours > 0 ? Math.round(sub.hours * ratio * 10000) / 10000 : null;
        return {
          ...c, ref_display, size_display, size_marker, formula_error: null,
          effective_qty, mat_cost, labor_rate: null, labor_cost, line_cost, hours,
          line_price: subUnitCost(mat_cost, effective_qty),
          _is_sub_assembly: true, _qty_is_size: true,
        };
      }

      const paramMap = c.param_map ? (() => { try { const p = JSON.parse(c.param_map); return (p && typeof p === 'object') ? p : null; } catch { return null; } })() : null;
      if (paramMap && Object.keys(paramMap).length > 0) {
        const mapped = c._sub_resolved_mapped;
        if (mapped?.param_not_found) {
          return { ...c, ref_display, size_display, size_marker, formula_error: `sub-product has no param "${mapped.param_not_found}"`, effective_qty, ...NO_COST, _is_sub_assembly: true };
        }
        if (mapped?.cycle_error) {
          return { ...c, ref_display, size_display, size_marker, formula_error: mapped.cycle_error, effective_qty, ...NO_COST, _is_sub_assembly: true };
        }
        if (!mapped || mapped.mat == null || !c._mapped_at) {
          return { ...c, ref_display, size_display, size_marker, formula_error: sizeDispErr, effective_qty, ...NO_COST, _is_sub_assembly: true };
        }
        // Evaluate each mapping formula in the CURRENT (local) parent env — exact,
        // since these formulas are over parent params only.
        const ratios = [];
        let mappingErr = null;
        for (const [subParam, formula] of Object.entries(paramMap)) {
          const errRef = {};
          const nowVal = evalFormula(formula, env, errRef);
          if (nowVal == null) { mappingErr = errRef.msg || `mapping for "${subParam}" unresolved: ${formula}`; break; }
          const atTypical = c._mapped_at[subParam];
          ratios.push(atTypical ? (nowVal / atTypical) : 1);
        }
        if (mappingErr) {
          return { ...c, ref_display, size_display, size_marker, formula_error: mappingErr, effective_qty, ...NO_COST, _is_sub_assembly: true };
        }
        const ratio = ratios.length ? ratios.reduce((a, b) => a + b, 0) / ratios.length : 1;
        const isTypical = Math.abs(ratio - 1) < 1e-9;
        const mat_cost   = Math.round(mapped.mat   * ratio * effective_qty * 100) / 100;
        const labor_cost = mapped.labor > 0 ? Math.round(mapped.labor * ratio * effective_qty * 100) / 100 : null;
        const line_cost  = Math.round((mat_cost + (labor_cost ?? 0)) * 100) / 100;
        const hours      = mapped.hours > 0 ? Math.round(mapped.hours * ratio * effective_qty * 10000) / 10000 : null;
        return {
          ...c, ref_display, size_display, formula_error: null,
          size_marker: isTypical ? size_marker : '≈ linear preview — exact on save',
          effective_qty, mat_cost, labor_rate: null, labor_cost, line_cost, hours,
          line_price: subUnitCost(mat_cost, effective_qty),
          _is_sub_assembly: true, _param_mapped: true,
        };
      }

      const sub = c._sub_resolved;
      if (sub?.cycle_error) {
        return { ...c, ref_display, size_display, size_marker, formula_error: sub.cycle_error, effective_qty: null, ...NO_COST, _is_sub_assembly: true };
      }
      if (!sub || sub.mat == null) {
        return { ...c, ref_display, size_display, size_marker, formula_error: sizeDispErr, effective_qty: null, ...NO_COST, _is_sub_assembly: true };
      }
      const mat_cost   = Math.round(sub.mat   * effective_qty * 100) / 100;
      const labor_cost = sub.labor > 0 ? Math.round(sub.labor * effective_qty * 100) / 100 : null;
      const line_cost  = Math.round((mat_cost + (labor_cost ?? 0)) * 100) / 100;
      const hours      = sub.hours > 0 ? Math.round(sub.hours * effective_qty * 10000) / 10000 : null;
      return { ...c, ref_display, size_display, size_marker, formula_error: null, effective_qty, mat_cost, labor_rate: null, labor_cost, line_cost, hours, line_price: subUnitCost(mat_cost, effective_qty), _is_sub_assembly: true };
    }

    if (c.kind !== 'line') {
      return { ...c, ref_display, size_display, size_marker, formula_error: sizeDispErr, effective_qty: null, ...NO_COST };
    }

    // Sized mode: resolve catalog line by NPS lookup from sized_lines.
    if (c.size_mode === 'sized' && c.size_expr) {
      const sizeErrRef = {};
      const sizeVal = evalFormula(c.size_expr, env, sizeErrRef);
      if (sizeVal == null) {
        const ferr = sizeErrRef.msg || `unresolved: ${c.size_expr}`;
        return { ...c, ref_display, size_display, size_marker, formula_error: ferr, effective_qty: null, line_price: null, line_unit: null, ...NO_COST };
      }
      const line = (c.sized_lines || []).find(l => l.size_value != null && Math.abs(l.size_value - sizeVal) < 0.001);
      if (!line || line.price == null) {
        return { ...c, ref_display, size_display, size_marker, formula_error: `no catalog line for ${sizeVal}"`, effective_qty: null, line_price: null, line_unit: null, ...NO_COST };
      }
      let effective_qty;
      if (c.qty_expr) {
        const qtyErrRef = {};
        effective_qty = evalFormula(c.qty_expr, env, qtyErrRef);
        if (effective_qty == null) {
          const ferr = qtyErrRef.msg || `qty unresolved: ${c.qty_expr}`;
          return { ...c, ref_display, size_display, size_marker, formula_error: ferr, effective_qty: null, line_price: line.price, line_unit: line.unit, ...NO_COST };
        }
      } else {
        effective_qty = c.base_qty;
      }
      // No pre-rounding of effective_qty here — matches server computeTypicalCost
      // sized/plain branch, which feeds unrounded qty into mat_cost. (The fab
      // branch that DID pre-round went with catalog_fabrication, 2026-08-18.)
      // Pre-rounding here caused a client/server
      // display mismatch on non-"nice" quantities (feet→metric migration, 2026-07-08).
      const gross = Math.round(effective_qty * line.price * 100) / 100;
      const lineL1 = line.l1 ?? c._family_l1;
      const { lh, lc, rate, uncharged } = clientLaborAddon({ l1: lineL1, _l1_class: c._l1_class }, line.hrs, effective_qty, laborRate, laborProductivity);
      // An HR line's cost IS labour: qty is hours and line.price is the rate.
      // clientLaborAddon returns lc:0 for HR BY DESIGN and leaves the bucketing to the
      // caller — product-db.js does exactly that on its sub-product path; this branch
      // never did, so the money landed in Material with Hours, $/Hr and Labor blank.
      // line_cost is identical either way; only the column it lands in changes.
      const isHR = classOf({ l1: lineL1, _l1_class: c._l1_class }) === 'HR';
      const mat_cost = isHR ? null : gross;   // null renders "—"; HR has no material, it is not zero material
      const line_cost = Math.round((isHR ? gross : gross + lc) * 100) / 100;
      return { ...c, ref_display, size_display, size_marker, formula_error: null, effective_qty, line_price: line.price, line_unit: line.unit, mat_cost, labor_rate: isHR ? line.price : rate, labor_cost: isHR ? gross : (lc || null), line_cost, hours: lh, uncharged_hours: uncharged || null };
    }

    if (c.line_price == null) {
      // Unpriced-line visibility (Alex, 2026-07-02): don't render a silent dead row —
      // attach a formula_error so the ⚠ cascade + productErrorCount pick it up.
      const noPriceErr = classOf(c) === 'MF'
        ? 'no pricing — fill fab fields (qty driver, rate, trade, MF) or a unit price'
        : 'no unit price on this line';
      return { ...c, ref_display, size_display, size_marker, formula_error: sizeDispErr || noPriceErr, effective_qty: null, ...NO_COST };
    }

    let effective_qty;
    if (c.qty_expr) {
      const qtyErrRef = {};
      effective_qty = evalFormula(c.qty_expr, env, qtyErrRef);
      if (effective_qty == null) {
        const ferr = qtyErrRef.msg || `qty unresolved: ${c.qty_expr}`;
        return { ...c, ref_display, size_display, size_marker, formula_error: ferr, effective_qty: null, ...NO_COST };
      }
    } else {
      // the dead size_mode='bind' branch (size_expr used as the QUANTITY) went
      // with the column — see sizeDisplay above. Mirrors computeTypicalCost.
      effective_qty = c.base_qty;
    }
    // No pre-rounding of effective_qty here — matches server computeTypicalCost
    // plain/sized branch, which feeds unrounded qty into mat_cost. (The fab
    // branch that DID pre-round went with catalog_fabrication, 2026-08-18.)
    // Pre-rounding here caused a client/server
    // display mismatch on non-"nice" quantities (feet→metric migration, 2026-07-08).
    const gross = Math.round(effective_qty * c.line_price * 100) / 100;
    const { lh, lc, rate, uncharged } = clientLaborAddon(c, c.line_hrs, effective_qty, laborRate, laborProductivity);
    // Same bucketing as the `line` branch above — an HR comp's cost is labour, not
    // material. Kept in both branches rather than hoisted: they differ in where the
    // price and the class come from, and a shared helper would hide that.
    const isHR = classOf(c) === 'HR';
    const mat_cost = isHR ? null : gross;   // null renders "—"; see the note above
    const line_cost = Math.round((isHR ? gross : gross + lc) * 100) / 100;
    return { ...c, ref_display, size_display, size_marker, formula_error: sizeDispErr, effective_qty, mat_cost, labor_rate: isHR ? c.line_price : rate, labor_cost: isHR ? gross : (lc || null), line_cost, hours: lh, uncharged_hours: uncharged || null };
  });
}
